import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, breadcrumbSchema, pageHead } from "@/lib/seo";
import { SITE_URL } from "@/lib/locale";

export const Route = createFileRoute("/en/services/performance-marketing/meta-ads/recruiting")({
  head: () => {
    const h = pageHead("pmMetaAdsRecruiting", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Meta Ads for Employee Recruitment",
          serviceType: "Recruiting Advertising",
          description:
            "Meta Ads for employee recruitment in care, skilled trades, logistics, manufacturing, hospitality, and education. 60–90% lower CPA than classical job portals.",
          url: `${SITE_URL}/en/services/performance-marketing/meta-ads/recruiting`,
          provider: {
            "@type": "Organization",
            name: "Calvarius GmbH",
            url: SITE_URL,
          },
          areaServed: [
            "Main-Spessart",
            "Würzburg",
            "Karlstadt am Main",
            "Aschaffenburg",
            "Frankfurt am Main",
            "Germany",
          ],
          audience: {
            "@type": "BusinessAudience",
            audienceType:
              "Mid-market companies with staffing needs in care, skilled trades, logistics, manufacturing, hospitality, and education",
          },
          isPartOf: {
            "@type": "Service",
            name: "Meta Ads",
            url: `${SITE_URL}/en/services/performance-marketing/meta-ads`,
          },
        }),
        jsonLdScript(
          breadcrumbSchema([
            { name: "Home", path: "/en" },
            { name: "Services", path: "/en/services" },
            { name: "Performance Marketing", path: "/en/services/performance-marketing" },
            { name: "Meta Ads", path: "/en/services/performance-marketing/meta-ads" },
            { name: "Recruiting", path: "/en/services/performance-marketing/meta-ads/recruiting" },
          ]),
        ),
      ],
    };
  },
  component: MetaAdsRecruitingEN,
});

const steps = [
  {
    num: "01",
    title: "Audience analysis (3–5 days)",
    body: "Defining the search profile together with HR: job role, geographic catchment area, demographic markers, media-usage behavior. Audit of existing recruiting channels (job portals, careers page, referral program) on cost-per-application and quality rate. Clear hypotheses where Meta Ads outperforms economically.",
  },
  {
    num: "02",
    title: "Funnel & landing setup (1–2 weeks)",
    body: "Building a dedicated application landing page — no PDF download, but a low-threshold form with two to four fields. Conversions API integration for lead tracking, mobile-first design, trust signals (location, team photos, benefits). Classical careers pages convert poorly on mobile recruiting traffic — we build a specific funnel stage.",
  },
  {
    num: "03",
    title: "Campaign launch & first iteration (week 3–4)",
    body: "Geo targeting on the defined catchment area, demographic layers, custom audiences from existing employee profiles for lookalike models. Creative variation in brand voice via picazzoo. First learning data after 7–10 days, first optimization wave after 14 days.",
  },
  {
    num: "04",
    title: "Scaling & maintenance (continuous)",
    body: "Continuous creative iteration on what produces applications — not what produces clicks. Seasonal adaptation to applicant behavior. Monthly reporting on cost-per-application and cost-per-hire, not platform CPCs. Operational management directly by the managing director.",
  },
];

const capabilities = [
  { name: "Geo targeting on catchment area", detail: "Radius · postal code · location" },
  { name: "Demographic profiling", detail: "Age · profession · interests" },
  { name: "Lookalike models", detail: "From existing employee profiles" },
  { name: "Application landing pages", detail: "Mobile-first · low-threshold" },
  { name: "Conversions API for leads", detail: "Server-side · match quality" },
  { name: "Creative production via picazzoo", detail: "Brand-true · scalable" },
  { name: "Industry CPA benchmarks", detail: "Care · trades · logistics" },
  { name: "Reporting on cost-per-hire", detail: "Economic, not platform" },
];

const audience = [
  { tag: "Primary", title: "Care & health", body: "Care homes, ambulatory care services, hospitals, medical centers. Classical job portals deliver increasingly empty pipelines for care workers — Meta Ads reaches the audience where they actually spend time." },
  { tag: "Primary", title: "Skilled trades & construction", body: "Electricians, plumbing, carpentry, masonry, painting. High visuality (workshop, projects), clear regional bond — both ideal for Meta Ads." },
  { tag: "Primary", title: "Logistics, manufacturing, hospitality", body: "Drivers, warehouse, shift production, service, kitchen. Large applicant volume with clear geographic and demographic markers. Meta Ads is often cheaper here than any print ad." },
  { tag: "Primary", title: "Education roles", body: "Daycare, after-school care, family care, social work. Audience is highly active on Meta — careers pages and job portals massively underperform." },
];

const realistic = [
  "Cost-per-application 60–90% below classical job portals is achievable in most setups — especially in fields with high competition for staff (care, trades, logistics).",
  "Applicant volume increase by a factor of 3–8x compared to careers page only is realistic when the landing page converts cleanly.",
  "Time-to-hire reduction of 30–50% through constant applicant flow instead of waiting on portal cycles.",
  "Economical from approximately €800/month media budget — below that, operational effort per application becomes too high.",
];

const value = [
  ["Regional precision instead of national scatter.", "Meta Ads enables geo targeting on the exact catchment area — no application spam from 200 km away."],
  ["Visibility where the audience spends time.", "Care workers, tradespeople, and logistics personnel rarely use job portals actively — on Instagram and Facebook they're there daily."],
  ["Low-threshold application funnel.", "Instead of uploading a PDF résumé: name, contact, one question. Applications come in because the hurdle is low."],
  ["Applicant quality steerable through targeting.", "We don't filter wrong applications via HR effort but via targeting discipline and funnel logic."],
  ["Monthly calculable instead of job-portal packages.", "You pay for actually reached audience, not for portal slots no one sees."],
  ["No set-and-forget.", "Recruiting performance changes with season, competition, and market — we steer actively."],
];

const related = [
  { to: "/en/services/performance-marketing/meta-ads", name: "Meta Ads (main page)", sub: "Performance marketing on Meta for e-commerce, B2C SaaS, and B2B" },
  { to: "/en/services/conversion-rate-optimization", name: "Conversion Rate Optimization", sub: "Application landing pages with clear funnel logic" },
  { to: "/en/services/picazzoo", name: "picazzoo", sub: "Creative production for recruiting ads in brand voice" },
];

function MetaAdsRecruitingEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Meta Ads · Recruiting"
          headline={<>Meta Ads for Employee Recruitment — <em>recruiting without scatter losses,</em> with targeting discipline.</>}
          lead="Classical job portals are losing impact in many fields — care, skilled trades, logistics, manufacturing, hospitality, and education roles find their applicants today on Instagram and Facebook. Calvarius has run Meta Ads for recruiting as an independent discipline since 2018: with geo targeting on the real catchment area, low-threshold application funnels, and 60–90 percent lower cost-per-application than classical portals."
          metaStrip={[
            "In the recruiting channel since 2018",
            "Economical from €800 media budget",
            "Up to 90% lower CPA vs. classical",
          ]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "Meta Ads", to: "/en/services/performance-marketing/meta-ads" },
            { label: "Recruiting" },
          ]}
        />

        <Section number="02" kicker="Standpoint" headline={<>Why classical recruiting <em>no longer works in many fields.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">The recruiting reality in care, skilled trades, logistics, and manufacturing has shifted quietly over the past years. Job portals like Stepstone, Indeed, or Monster were originally built for knowledge workers — people who actively search for jobs, keep a résumé ready, and scroll listings on a desktop platform. In professions with high staff competition, most qualified workers don't search actively. They are busy, they are on site, and they don't spend their attention time on career portals — but on Instagram, Facebook, and in messaging services.</p>
              <p className="mb-5">Meta Ads reaches these audiences because the platform receives several hours of attention per day — and because geo-, age-, and interest-targeting allow precision no job portal can offer. This makes Meta Ads the most economically rational recruiting infrastructure for mid-market mandates whose hiring needs must be steered seasonally, regionally, and qualitatively with precision.</p>
              <p>Calvarius has run recruiting on Meta Ads as an independent discipline within the Meta ecosystem since 2018. We understand the specific mechanics an application funnel needs — an application landing page is not a sales landing page, a recruiting creative is not a performance creative, and the right <Link to="/en/glossary/attribution-model" className="underline">attribution model</Link> for applications differs from the one for e-commerce conversions.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience fit" headline={<>For whom Meta Ads recruiting <em>makes economic sense.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How we build <em>recruiting mandates.</em></>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our <em>recruiting setup</em> concretely contains.</>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="What's realistic" headline={<>Realistic results in <em>recruiting mandates.</em></>}
          caption="These ranges are empirical from mandates in care, trades, logistics, and hospitality.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What you gain" headline={<>What you get from <em>Meta Ads recruiting.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing/meta-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Back to Meta Ads main page
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="09" kicker="Get concrete">
          <CTABlock
            headline={<>Recruiting audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation we clarify whether Meta Ads makes economic sense for your staffing needs — and if so, with what setup. Free, non-binding, no sales pitch."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Back to Meta Ads", to: "/en/services/performance-marketing/meta-ads" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
